import * as React from "react"

import { cn } from "@/lib/utils"
import {
  DEFAULT_NETWORK_ASSET_BASE,
  NETWORKS,
  type NetworkSlug,
  type NetworkVariant,
} from "@/lib/networks"

/**
 * One network icon, either variant, behind one prop.
 *
 * Three treatments sit underneath, all served through one prop:
 *
 *   · solid — a glyph in the `dlvr-networks` icon font: monochrome, takes CSS
 *             `color`, uniform optical weight.
 *   · color — the svg-outputs badge artwork: a brand disc with the mark knocked
 *             out, uniformly square, served as an <img>.
 *   · mark  — the purpose-drawn full-colour artwork with no disc, tight-cropped
 *             at its own aspect, served as an <img>.
 *
 * `color` and `mark` are different visual treatments, not two encodings of the
 * same thing — a row of badges lines up, a row of marks varies in optical weight.
 * Callers pick per surface; this only resolves availability.
 *
 * The solid class namespace is `ds-network`, matching the sibling font's `ds-icon`.
 *
 * Not every network has both. eBay has no glyph; Facebook Groups and
 * Unsplash have no colour artwork. Every slug has at least ONE, so an unavailable
 * variant silently falls back to the other rather than rendering nothing. Read
 * `NETWORKS[name]` if a caller needs to branch on what actually exists.
 *
 * A single `size` drives both variants. That works because the two pipelines were
 * matched deliberately: the font's ink fills 820/1000 of its em box and the badge
 * artwork fills ~84% of its viewBox, so at the same `size` they render within
 * ~2% of each other instead of visibly disagreeing.
 */

export interface NetworksProps extends Omit<React.ComponentProps<"span">, "children"> {
  name: NetworkSlug
  /** Defaults to "color". Falls back to the next available treatment. */
  variant?: NetworkVariant
  /** Rendered size in px, driving font-size (solid) or box (color). */
  size?: number
  /** Asset host for the colour variant. Pass the CDN base in production. */
  assetBaseUrl?: string
  /**
   * Accessible name. Omit for decorative use (the default) — the icon is hidden
   * from assistive tech, which is right when a visible text label sits beside it.
   * Pass a string, or `true` to use the registry label, when the icon is the only
   * thing identifying the network.
   */
  label?: string | true
}

export function Networks({
  name,
  variant = "color",
  size,
  assetBaseUrl = DEFAULT_NETWORK_ASSET_BASE,
  label,
  className,
  style,
  ...props
}: NetworksProps) {
  const entry = NETWORKS[name]

  if (!entry) {
    // Unreachable through the NetworkSlug type, but reachable from untyped .jsx
    // callers — of which this codebase has many. Warned unconditionally: there is
    // no NODE_ENV guard idiom here (no @types/node), and a bad slug is always a
    // programming error rather than a user-facing state.
    console.warn(`<Networks>: unknown network "${name}"`)
    return null
  }

  /*
   * Honour the request, else fall through to the nearest treatment. Ordered
   * rather than nested so adding a fourth stays a data change: a requested
   * treatment degrades to the most similar one it has (mark -> color, both
   * full-colour <img>; solid -> color only as a last resort). Every slug has at
   * least one, which check-parity enforces, so this always resolves.
   */
  const available: Record<NetworkVariant, boolean> = {
    solid: entry.solid,
    color: Boolean(entry.color),
    mark: Boolean(entry.mark),
  }
  const fallback: Record<NetworkVariant, NetworkVariant[]> = {
    solid: ["solid", "color", "mark"],
    color: ["color", "mark", "solid"],
    mark: ["mark", "color", "solid"],
  }
  const resolved = fallback[variant].find((v) => available[v]) as NetworkVariant

  const name_ = label === true ? entry.label : label
  const decorative = name_ === undefined

  if (resolved === "solid") {
    return (
      <i
        className={cn("ds-network", `ds-network-${name}`, className)}
        style={size ? { fontSize: `${size}px`, ...style } : style}
        aria-hidden={decorative || undefined}
        aria-label={name_}
        role={decorative ? undefined : "img"}
        {...props}
      />
    )
  }

  const base = assetBaseUrl.replace(/\/+$/, "")
  const src = resolved === "mark" ? entry.mark : entry.color

  return (
    <img
      src={`${base}/${src}`}
      /*
       * `size` means BOX size, so it pins both dimensions and object-contain fits
       * the artwork inside — consistent with the solid glyph's square em box and
       * the square badges. Marks are tight-cropped at their own aspect (facebook
       * 0.53, youtube 1.33), so this letterboxes them with transparent padding
       * rather than distorting, which is what keeps a column of them aligned.
       *
       * Wordmarks are the exception: at ~2.5 aspect a square box fits them by
       * width and they land at ~40% of their neighbours' height, so they get the
       * height only and let the width run.
       */
      width={size && !entry.wordmark ? size : undefined}
      height={size}
      /*
       * The size-6 fallback is load-bearing: with no `size` and no sizing class,
       * an <img> renders at intrinsic size — 121px for a badge, 363px for the
       * Instagram mark — which blows out whatever lays it out. `className` comes
       * last so a caller can still override it.
       */
      className={cn("tw:object-contain", !size && "tw:size-6", className)}
      style={style}
      alt={name_ ?? ""}
      {...(props as React.ComponentProps<"img">)}
    />
  )
}

/**
 * A network's solid glyph knocked out of a brand-colour disc — the same treatment
 * `.dlvr-icon.<service>` gives it elsewhere in the app, with the brand colours
 * taken from the named vars in dlvr-vars.styl via the network registry.
 *
 * The badge treatment, as against `<Networks>`'s three bare treatments: use this
 * where the mark stands in for an ACCOUNT (a tile avatar, a destination badge),
 * and a bare `<Networks>` where it labels the network itself (a filter row, a
 * legend). Unlike the `color` and `mark` variants it needs no asset host, since
 * the glyph is a font — which is what lets the legacy app render it, having no
 * JS-side static host to pass as `assetBaseUrl`.
 *
 * The ring is what separates the disc from whatever sits behind it. The
 * svg-outputs artwork had a white disc and hairline border baked into the SVG; a
 * flat brand disc has neither, so on an avatar it would read as a blob without
 * this.
 *
 * `glyph` is sized separately rather than as a fixed fraction of `size` because
 * a responsive badge sizes by class (size-14 -> size-[75px]), and there is no
 * pure-CSS way here to derive a font-size from an element's own width —
 * container queries don't compile reliably under Tailwind's prefix mode in this
 * repo. A numeric `size` has no such problem and defaults its glyph to 0.6x,
 * which is the ratio the drawn badges use (13/22, 34/56, 45/75).
 *
 * eBay is the one slug with no glyph, so it falls back to an <img> against the
 * default asset host. It's an input-only subtype and a wordmark besides, so it
 * has no business on an account badge in the first place.
 */
export function NetworkBadge({
  network,
  className,
  size = "tw:size-6",
  glyph,
  decorative = false,
}: {
  network: NetworkSlug
  className?: string
  /** Disc edge: px, or a Tailwind size class where it must be responsive. */
  size?: number | string
  /** Glyph size: px, or a Tailwind text class. Defaults per `size`, above. */
  glyph?: number | string
  /**
   * Hides the badge from assistive tech. Labelled by default — on a tile it
   * carries the only mention of the network — but a caller that already names
   * the network in visible text beside it wants this, or the row is announced
   * twice. `Networks` spells the same idea as its `label` prop.
   */
  decorative?: boolean
}) {
  const entry = NETWORKS[network]

  const sizeClass = typeof size === "string" ? size : undefined
  const glyphPx =
    typeof glyph === "number"
      ? glyph
      : typeof size === "number" && glyph === undefined
        ? Math.round(size * 0.6)
        : undefined
  const glyphClass =
    typeof glyph === "string" ? glyph : sizeClass && !glyph ? "tw:text-[15px]" : undefined

  return (
    <span
      // The tile states the account name and subtype but never the network, so
      // this carries the only mention of it. The glyph inside stays decorative.
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : entry.label}
      aria-hidden={decorative || undefined}
      className={cn(
        // Always white, including on the light discs (Blogger/Feed #F68F3E,
        // Slack #8dd2b8, Snapchat #EBE805). They fall under WCAG 1.4.11's 3:1
        // non-text bar, but that bar exempts logotypes, and white on the brand
        // colour is how these marks are actually presented — a dark-ink exception
        // read as a rendering bug beside their siblings.
        "tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:ring-2 tw:ring-background tw:text-white",
        sizeClass,
        glyphClass,
        className
      )}
      style={{
        backgroundColor: entry.brand,
        ...(typeof size === "number" ? { width: size, height: size } : {}),
        ...(glyphPx !== undefined ? { fontSize: glyphPx } : {}),
      }}
    >
      <Networks name={network} variant="solid" />
    </span>
  )
}
